import {
    AudioSearchResponse,
    GraphicSearchResponse,
    VideoSearchResponse,
    TypeaheadResponse,
    WebSearchApiSearchAudioRequest,
    WebSearchApiSearchVideoRequest,
    WebSearchApiSearchGraphicRequest,
    SearchSimilarApiGetSimilarAudioItemsRequest,
    GetSimilarAudioItems200Response,
    AudioDetailsResponse,
} from '@videoblocks/search-gateway-client';

import { audioRules, defaultRules, imageRules, similarSearchAudioRules, videoRules } from './rules';

export interface BaseSearchRequest {
    algorithm?: string;
    searchTerm?: string;
    numResults?: number;
    pageNumber?: number;
}

type SearchParams = Record<string, string | string[] | undefined>;

export interface Rule<TInput, TOutput> {
    searchKey: string; // the query-string key
    transform?: (input: TInput) => TOutput;
}

export type RuleSet = Record<string, Rule<unknown, unknown>>;

// TODO: update graphic to images for typeahead suggestion?
export type SearchGatewayContentClass = 'video' | 'audio' | 'graphic';
export interface ISearchGatewayClient {
    buildRequest<Req extends BaseSearchRequest>(
        params: Req,
        rules:
            | typeof defaultRules
            | typeof audioRules
            | typeof imageRules
            | typeof videoRules
            | typeof similarSearchAudioRules,
    ): Req;
    logError(error: unknown, props: SearchParams): void;
    searchAudio(props: WebSearchApiSearchAudioRequest): Promise<AudioSearchResponse | undefined>;
    searchVideo(props: WebSearchApiSearchVideoRequest): Promise<VideoSearchResponse | undefined>;
    searchImages(props: WebSearchApiSearchGraphicRequest): Promise<GraphicSearchResponse | undefined>;
    getSearchAudioRequest(props: WebSearchApiSearchAudioRequest): WebSearchApiSearchAudioRequest;
    getTypeAheadSuggestion(
        contentClass: SearchGatewayContentClass,
        keywords?: string,
    ): Promise<TypeaheadResponse | undefined>;
    searchSimilarAudio(
        params: SearchSimilarApiGetSimilarAudioItemsRequest,
    ): Promise<GetSimilarAudioItems200Response | undefined>;
    getAudioDetailsByUrlId(urlId: string): Promise<AudioDetailsResponse>;
}

export const SearchOrigins = {
    FILTERS: 'filters',
    SEARCH_BAR: 'search_bar',
    SORT_BY: 'sort_by',
    TYPE_AHEAD: 'type_ahead',
    STOCK_ITEM_CARD: 'stock_item_card',
    UNKNOWN: '(unknown)',
    HOME: 'home',
    TOP_NAV: 'top_nav',
    DETAILS_PAGE: 'details_page',
    RELATED_SEARCH: 'related-search',
    PAGINATION: 'pagination',
    ARTIST: 'artist',
    APPLIED_FILTERS: 'applied_filters',
    AUTOCORRECT: 'autocorrect',
} as const;

export type SearchOrigins = (typeof SearchOrigins)[keyof typeof SearchOrigins];

// type of content tied to a specific content class
export const SearchFilterContentTypes = {
    All_videos_content_type: 'all-videos',
    All_audio_content_type: 'all-audio',
    All_images_content_type: 'all-images',
    // video
    Footage: 'footage',
    Motion_bgs: 'motion-backgrounds',
    Templates: 'templates',
    Premiere_pro_templates: 'premiere-pro-templates',
    After_effects: 'after-effects-templates',
    Davinci_resolve: 'davinci-resolve-templates',
    Apple_motion: 'apple-motion-templates',
    // audio
    Music: 'music',
    Sound_effects: 'sfx',
    // images
    Photos: 'photos',
    Snapshots: 'snapshots',
    Vectors: 'vectors',
    Illustrations: 'illustrations',
} as const;

export type SearchFilterContentTypes = (typeof SearchFilterContentTypes)[keyof typeof SearchFilterContentTypes];
export const AudioSearchFilterContentTypes = {
    All_audio_content_type: SearchFilterContentTypes.All_audio_content_type,
    Music: SearchFilterContentTypes.Music,
    Sound_effects: SearchFilterContentTypes.Sound_effects,
};
export const VideoSearchFilterContentTypes = {
    All_videos_content_type: SearchFilterContentTypes.All_videos_content_type,
    Footage: SearchFilterContentTypes.Footage,
    Motion_bgs: SearchFilterContentTypes.Motion_bgs,
    Templates: SearchFilterContentTypes.Templates,
    Premiere_pro_templates: SearchFilterContentTypes.Premiere_pro_templates,
    After_effects: SearchFilterContentTypes.After_effects,
    Davinci_resolve: SearchFilterContentTypes.Davinci_resolve,
    Apple_motion: SearchFilterContentTypes.Apple_motion,
};
export const ImageSearchFilterContentTypes = {
    Snapshots: SearchFilterContentTypes.Snapshots,
    Vectors: SearchFilterContentTypes.Vectors,
    Illustrations: SearchFilterContentTypes.Illustrations,
};

// Consumer-facing, typically in the search params
export const SearchFilterMediaTypes = {
    All_videos_content_type: 'all-video',
    All_audio_content_type: 'audio',
    All_images_content_type: 'images',
    //video
    Video: 'video',
    Footage: 'footage',
    Templates: 'templates',
    Motion_graphics: 'motion-graphics',
    Backgrounds: 'backgrounds',
    Premiere_pro_templates: 'premiere-pro-templates',
    After_effects: 'after-effects-templates',
    Davinci_resolve: 'davinci-resolve-templates',
    Apple_motion: 'apple-motion-templates',
    //audio
    Music: 'music',
    Sound_effects: 'sound-effects',
    // images
    Photos: 'photos',
    Snapshots: 'snapshots',
    Vectors: 'vectors',
    Illustrations: 'illustrations',
} as const;

export type SearchFilterMediaTypes = (typeof SearchFilterMediaTypes)[keyof typeof SearchFilterMediaTypes];
export type AudioSearchFilterMediaTypes = (typeof SearchFilterMediaTypes)[
    | 'All_audio_content_type'
    | 'Music'
    | 'Sound_effects'];
export type VideoSearchFilterMediaTypes = (typeof SearchFilterMediaTypes)[
    | 'All_videos_content_type'
    | 'Footage'
    | 'Motion_graphics'
    | 'Backgrounds'
    | 'Templates'
    | 'Premiere_pro_templates'
    | 'After_effects'
    | 'Davinci_resolve'
    | 'Apple_motion'];
export type ImageSearchFilterMediaTypes = (typeof SearchFilterMediaTypes)['Snapshots' | 'Vectors' | 'Illustrations'];

export type SearchOption = {
    id: SearchFilterContentTypes;
    label: string;
    placeholderText: string;
    videoSrc?: string;
    posterSrc?: string;
};

// TODO: update with other search supported query params
export const SearchUrlParamKeys = {
    MEDIA_TYPE: 'media-type',
    SEARCH_ORIGIN: 'search-origin',
    CONTENT_CLASS: 'content-class',
    SEARCH_TERM: 'search_term',
    PAGE_NUMBER: 'page',
    NUM_RESULTS: 'num_results',
    SORT_BY: 'sort_by',
    ORIENTATION: 'orientation',
    USAGE_RIGHTS: 'usage_rights',
    CATEGORIES: 'categories',
    SEARCH_SIMILAR_ID: 'search_similar_id',
    // audio
    VOCALS: 'vocals',
    DURATION_MAX: 'duration_max',
    DURATION_MIN: 'duration_min',
    ARTIST_IDS: 'portal_artist_ids',
    // TODO: add video params
    // TODO: add image params
} as const;

export type SearchUrlParamKeys = (typeof SearchUrlParamKeys)[keyof typeof SearchUrlParamKeys];
